import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ClikArtItem } from '@/api/types/clikart'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { CenterCanvasHandle } from '@/components/app/editor/center-canvas'
import { buildPlacedClikArtShape } from '@/lib/clikart/build-placed-clikart-shape'
import {
  CLIKART_DRAG_GHOST_OPACITY,
  CLIKART_KEYBOARD_DEFAULT_CENTER_FRACTION,
} from '@/lib/clikart/clikart-placement-config'
import { computePhotoPlacementSize } from '@/lib/photos/photo-placement-geometry'
import { loadImageNaturalSize } from '@/lib/photos/photo-fill-resolution'
import { LINKED_TEXT_CLIKART_DROP_BLOCKED_MESSAGE } from '@/lib/clikart/linked-text-clikart-drop-policy'
import type { GridSnapLiveRect } from '@/lib/guides/grid-snap'
import { ClikArtDragGhost } from '@/components/app/clikart/clikart-drag-ghost'
import {
  type PlacementCanvasApi,
  type PlacementDropBlockedReason,
  type PlacementDropHit,
  type PlacementIntrinsicSize,
  usePlacementSession,
} from '@/hooks/use-placement-session'

/** Why a hit drop target may not receive the ClikArt. Currently only text-link chain membership. */
export type ClikArtDropBlockedReason = PlacementDropBlockedReason

/**
 * The topmost drop target under the cursor. A blocked target is still reported — it suppresses
 * the valid-target highlight and refuses the drop outright (mirrors `PhotoDropHit`'s own
 * WMPCM-8516 rationale — see `usePlacementSession`).
 */
export type ClikArtDropHit = PlacementDropHit

export type ClikArtPlacementCanvasApi = PlacementCanvasApi

type ClikArtPlacementContextValue = {
  beginPointerDrag: (
    item: ClikArtItem,
    clientX: number,
    clientY: number,
    thumbRect: DOMRect,
    pointerId: number,
    pointerCaptureEl?: HTMLElement | null
  ) => void
  beginKeyboardCarry: (item: ClikArtItem, thumbRect: DOMRect) => void
  cancelDrag: () => void
  isDraggingClikArt: boolean
  draggingArtId: string | null
  isKeyboardCarry: boolean
  registerCanvasApi: (api: ClikArtPlacementCanvasApi | null) => void
  /** shapeId of the shape under the cursor that is a valid ClikArt drop target, or null. */
  dropTargetShapeId: string | null
}

const ClikArtPlacementContext = createContext<ClikArtPlacementContextValue | null>(null)

export function useClikArtPlacementOptional(): ClikArtPlacementContextValue | null {
  return useContext(ClikArtPlacementContext)
}

type ClikArtPlacementProviderProps = {
  children: React.ReactNode
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  centerCanvasRef: React.RefObject<CenterCanvasHandle | null>
  snapToGrid?: boolean
  gridSnapStep?: number
  gridSnapLiveRects?: GridSnapLiveRect[]
  onScreenReaderAnnounce?: (message: string) => void
  /** Called when the user drops a ClikArt onto a fillable shape. */
  onFillShape?: (
    shapeId: string,
    item: ClikArtItem,
    kindId: number,
    dropCanvasPoint: { canvasX: number; canvasY: number },
    intrinsicSize: { width: number; height: number } | null
  ) => void
  /** Called when the drop landed on a target that refuses ClikArt. Nothing is placed — the toast
   *  is raised by the consumer (`home.tsx`), matching the photo placement convention. */
  onBlockedDrop?: (reason: ClikArtDropBlockedReason) => void
}

/**
 * ClikArt items already carry `pixelWidth`/`pixelHeight` directly from the gallery payload
 * (`/clikart/search`/`/clikart/categories`), unlike Photos which only get a tray thumbnail's
 * dimensions up front and need a follow-up candid-detail fetch (`primeRealResolution` in
 * `photo-placement-context.tsx`) for the real ones. So there is only one source to prime here —
 * the item's own reported pixels, with a single image-load fallback when they're missing.
 */
function primeIntrinsicSize(
  item: ClikArtItem,
  sizeRef: React.MutableRefObject<PlacementIntrinsicSize | null>
): void {
  if (item.pixelWidth && item.pixelHeight) {
    sizeRef.current = { width: item.pixelWidth, height: item.pixelHeight }
    return
  }

  void loadImageNaturalSize(item.scrUrl).then((size) => {
    if (size) sizeRef.current = size
  })
}

export function ClikArtPlacementProvider({
  children,
  shapeManagerRef,
  centerCanvasRef,
  snapToGrid,
  gridSnapStep,
  gridSnapLiveRects,
  onScreenReaderAnnounce,
  onFillShape,
  onBlockedDrop,
}: ClikArtPlacementProviderProps) {
  const intrinsicSizeRef = useRef<PlacementIntrinsicSize | null>(null)

  const session = usePlacementSession<ClikArtItem>({
    centerCanvasRef,
    intrinsicSizeRef,
    snapToGrid,
    gridSnapStep,
    gridSnapLiveRects,
    onScreenReaderAnnounce,
    onBlockedDrop,
    blockedDropMessage: LINKED_TEXT_CLIKART_DROP_BLOCKED_MESSAGE,
    keyboardCenterFraction: CLIKART_KEYBOARD_DEFAULT_CENTER_FRACTION,
    displayNameOf: (target) => target.displayName,
    onDragStart: (target) => primeIntrinsicSize(target, intrinsicSizeRef),
    placeAtCanvas: (target, centerX, centerY, { api, announce }) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const pixelWidth = target.pixelWidth ?? intrinsicSizeRef.current?.width
      const pixelHeight = target.pixelHeight ?? intrinsicSizeRef.current?.height
      const shape = buildPlacedClikArtShape({
        objectId: crypto.randomUUID().replace(/-/g, ''),
        item: target,
        centerX,
        centerY,
        canvasWidth: api.bleedWidth,
        canvasHeight: api.bleedHeight,
        intrinsicWidth: pixelWidth,
        intrinsicHeight: pixelHeight,
        size: computePhotoPlacementSize(pixelWidth, pixelHeight),
      })

      mgr.addShapeData({ ...shape, placementAnimateIn: true }, { selectAfterCreate: true })
      announce(`Placed ${target.displayName} on canvas`)
    },
    fillShape: (target, hit, point, { announce }) => {
      const intrinsicSize =
        target.pixelWidth && target.pixelHeight
          ? { width: target.pixelWidth, height: target.pixelHeight }
          : intrinsicSizeRef.current

      onFillShape?.(hit.shapeId, target, hit.kindId, point, intrinsicSize)
      announce(`Filled shape with ${target.displayName}`)
    },
  })

  const { item, beginPointerDrag, beginKeyboardCarry, cancelDrag, registerCanvasApi } = session
  const { isActive, isKeyboardCarry, dropTargetShapeId, ghost } = session

  // ClikArt has no touch long-press, so the session's `pointerType` parameter is pinned to mouse.
  const beginClikArtPointerDrag = useCallback<ClikArtPlacementContextValue['beginPointerDrag']>(
    (target, clientX, clientY, rect, pointerId, pointerCaptureEl = null) =>
      beginPointerDrag(target, clientX, clientY, rect, pointerId, 'mouse', pointerCaptureEl),
    [beginPointerDrag]
  )

  const contextValue = useMemo<ClikArtPlacementContextValue>(
    () => ({
      beginPointerDrag: beginClikArtPointerDrag,
      beginKeyboardCarry,
      cancelDrag,
      isDraggingClikArt: isActive,
      draggingArtId: item?.artId ?? null,
      isKeyboardCarry,
      registerCanvasApi,
      dropTargetShapeId,
    }),
    [
      beginClikArtPointerDrag,
      beginKeyboardCarry,
      cancelDrag,
      dropTargetShapeId,
      isActive,
      isKeyboardCarry,
      item?.artId,
      registerCanvasApi,
    ]
  )

  return (
    <ClikArtPlacementContext.Provider value={contextValue}>
      {children}
      {item != null &&
        ghost != null &&
        createPortal(
          <ClikArtDragGhost item={item} opacity={CLIKART_DRAG_GHOST_OPACITY} {...ghost} />,
          document.body
        )}
    </ClikArtPlacementContext.Provider>
  )
}
